import { FOCUS_RING_NO_RADIUS } from '@amelio/ui-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Pages one evaluation panel's list. The three panels of the "Évaluation" tab show the same kind
 * of list at three page sizes, so the paging lives here once rather than in each of them.
 *
 * `visible` is what the panel renders; `pageIndex` is clamped back into range when the list
 * shrinks under the reader -- a deleted evaluation must not leave them on a page that no longer
 * exists.
 */
export const useEvaluationPager = <T,>(items: T[] | undefined, pageSize: number) => {
    const [pageIndex, setPageIndex] = useState(0);

    useEffect(() => {
        if (!items) return;
        const lastPage = (Math.ceil(items.length / pageSize) || 1) - 1;
        if (pageIndex > lastPage) {
            setPageIndex(Math.max(0, lastPage));
        }
    }, [items, pageIndex, pageSize]);

    const start = pageIndex * pageSize;

    return {
        pageIndex,
        setPageIndex,
        pageCount: Math.ceil((items?.length ?? 0) / pageSize),
        visible: items?.slice(start, start + pageSize) ?? [],
    };
};

interface EvaluationPagerProps {
    pageIndex: number;
    pageCount: number;
    onPickPage: (pageIndex: number) => void;
}

/**
 * The row of dots under an evaluation panel's list. Nothing is drawn for a single page: one dot
 * would offer a choice that does not exist.
 */
export const EvaluationPager = ({ pageIndex, pageCount, onPickPage }: EvaluationPagerProps) => {
    const { t } = useTranslation();

    if (pageCount <= 1) {
        return null;
    }

    return (
        <div className={'flex justify-center'}>
            <div className={'flex items-center gap-2'}>
                {Array.from({ length: pageCount }).map((_, page) => (
                    <button
                        key={`pager-dot-${page}`}
                        type={'button'}
                        aria-label={t('performanceProfile.evaluations.pagerGoToPage', {
                            page: page + 1,
                        })}
                        aria-current={pageIndex === page ? 'page' : undefined}
                        onClick={() => onPickPage(page)}
                        // `FOCUS_RING_NO_RADIUS`, not `FOCUS_RING`: a dot owns its own radius, and
                        // the shared `rounded-lg` would be emitted after `rounded-full` and win.
                        className={`${FOCUS_RING_NO_RADIUS} h-3 w-3 rounded-full transition-colors ${
                            pageIndex === page
                                ? 'bg-primary'
                                : 'bg-growth-line hover:bg-obj-stepper'
                        }`}
                    />
                ))}
            </div>
        </div>
    );
};
